import { useEffect, useRef } from 'react';
import { createLogger } from '@xanectech/lib';
import { AuthService } from '../services/AuthService';
import { secureSession } from '../lib/secure-session';

const logger = createLogger('Auth - usePortalLoginOutcome');
const PORTAL_OUTCOME_MANAGED_SLUGS = new Set(['xtech-admin', 'xtech-company']);
const SESSION_STORAGE_KEY = 'portal_login_logged';

type PortalLoginOutcome = 'pending' | 'success' | 'denied';

interface UsePortalLoginOutcomeOptions {
  outcome: PortalLoginOutcome;
  portal: string;
  userId?: string | null;
  email?: string | null;
  currentRole?: string | null;
  denialReason?: string;
  requestedPath?: string;
}

function getLoggedKey(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setLoggedKey(key: string): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, key);
  } catch {
    // sessionStorage unavailable — proceed without persistence
  }
}

export const usePortalLoginOutcome = ({
  outcome,
  portal,
  userId,
  email,
  currentRole,
  denialReason,
  requestedPath,
}: UsePortalLoginOutcomeOptions): void => {
  const lastOutcomeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (outcome === 'pending' || !userId) {
      return;
    }

    const safeEmail = email ?? 'unknown@email.com';

    const outcomeKey = [userId, portal, outcome, denialReason ?? 'none'].join(
      ':',
    );

    // Deduplicate within the same mount
    if (lastOutcomeKeyRef.current === outcomeKey) {
      return;
    }

    // Deduplicate across page refreshes — only log once per login session
    if (getLoggedKey() === outcomeKey) {
      lastOutcomeKeyRef.current = outcomeKey;
      return;
    }

    lastOutcomeKeyRef.current = outcomeKey;
    setLoggedKey(outcomeKey);

    void (async () => {
      try {
        if (outcome === 'success') {
          if (PORTAL_OUTCOME_MANAGED_SLUGS.has(portal)) {
            await secureSession.logSecurityEvent('user_login', {
              current_role: currentRole,
              email: safeEmail,
              login_method: 'password',
              login_successful: true,
              occurred_at: new Date().toISOString(),
              portal,
              requested_path: requestedPath ?? window.location.pathname,
              user_agent: navigator.userAgent,
            });
          }

          await AuthService.logLogin({
            email: safeEmail,
            loginSuccessful: true,
            portal,
            userId,
          });
          return;
        }

        await AuthService.logLogin({
          email: safeEmail,
          loginSuccessful: false,
          portal,
          userId,
        });

        await AuthService.logPortalAccessDenied({
          currentRole,
          denialReason: denialReason ?? 'portal_access_denied',
          email: safeEmail,
          portal,
          requestedPath,
          userId,
        });
      } catch (error) {
        lastOutcomeKeyRef.current = null;
        logger.error('Failed to record portal login outcome', {
          error: error instanceof Error ? error.message : String(error),
          outcome,
          portal,
          userId,
        });
      }
    })();
  }, [
    currentRole,
    denialReason,
    email,
    outcome,
    portal,
    requestedPath,
    userId,
  ]);
};
